import { useEffect, useRef, useCallback, useState } from 'react';
import anime from 'animejs';

export interface ScrollAnimationOptions {
  trigger?: 'onLoad' | 'onScroll';
  delay?: number;
  duration?: number;
  ease?: string;
  direction?: 'left' | 'right' | 'up' | 'down' | 'scale' | 'rotate' | 'fade';
  stagger?: number;
  threshold?: number;
  repeat?: boolean;
  rootMargin?: string;
  once?: boolean;
}

export const useScrollAnimation = (options: ScrollAnimationOptions = {}) => {
  const {
    threshold = 0.1,
    rootMargin = '0px 0px -50px 0px',
    once = true
  } = options;

  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      {
        threshold,
        rootMargin,
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, once]);

  return [ref, isVisible] as const;
};

export const useParallaxEffect = (speed: number = 0.3) => {
  const elementRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrolled = window.pageYOffset;
          const parallax = scrolled * speed;
          
          if (element) {
            element.style.transform = `translateY(${parallax}px)`;
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [speed]);

  return elementRef;
};

export const useTypewriterEffect = (text: string, speed: number = 30) => {
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;
    element.textContent = '';

    anime({
      targets: { count: 0 },
      count: text.length,
      duration: text.length * speed,
      easing: 'linear',
      update: function(anim: any) {
        const count = Math.floor(anim.animatables[0].target.count);
        element.textContent = text.substring(0, count);
      }
    });
  }, [text, speed]);

  return elementRef;
};

export const useWaveAnimation = (amplitude: number = 15, frequency: number = 0.015) => {
  const elementRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!elementRef.current) return;

    const element = elementRef.current;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = (Date.now() - startTime) * frequency;
      const y = Math.sin(elapsed) * amplitude;
      
      element.style.transform = `translateY(${y}px)`;
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [amplitude, frequency]);

  return elementRef;
};

// Hook for multiple elements with stagger effect
export const useScrollStagger = (elements: number, options: ScrollAnimationOptions = {}) => {
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleElements, setVisibleElements] = useState<boolean[]>(
    new Array(elements).fill(false)
  );

  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    refs.current.forEach((element, index) => {
      if (!element) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisibleElements(prev => {
              const newState = [...prev];
              newState[index] = true;
              return newState;
            });
            if (options.once !== false) {
              observer.unobserve(element);
            }
          } else if (options.once === false) {
            setVisibleElements(prev => {
              const newState = [...prev];
              newState[index] = false;
              return newState;
            });
          }
        },
        {
          threshold: options.threshold || 0.1,
          rootMargin: options.rootMargin || '0px 0px -50px 0px',
        }
      );

      observer.observe(element);
      observers.push(observer);
    });

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [elements, options.threshold, options.rootMargin, options.once]);

  const setRef = (index: number) => (el: HTMLDivElement | null) => {
    refs.current[index] = el;
  };

  return { setRef, visibleElements };
}; 